import { useEffect, useState } from "react";
import { Button, EnvBadge, ErrorNotice, Field, Input, Loading, Panel, Segmented, useToast } from "../../components/ui";
import { gameApi } from "../../lib/api";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { useClient } from "../../lib/session";
import { formatGameTime } from "../../lib/time";
import type { Clock, Env } from "../../lib/types";
import type { GameTabProps } from "./Game";

type Mode = Clock["mode"];

export function GameClock({ game, env }: GameTabProps) {
  const api = gameApi(useClient(), { gameId: game.id, env });
  const toast = useToast();
  const q = useQuery(`dev:clock:${game.id}:${env}`, () => api.clock(), { refreshMs: 5000 });
  const c = q.data;
  const [mode, setMode] = useState<Mode>("real_time");
  const [speed, setSpeed] = useState(60);
  const [tz, setTz] = useState("UTC");
  useEffect(() => {
    if (!c) return;
    setMode(c.mode);
    setSpeed(c.mode === "scaled" ? c.speed : 60);
    setTz(c.time_zone);
  }, [c?.mode, c?.speed, c?.time_zone]);
  const save = useAction(() => api.setClock({ mode, speed: mode === "scaled" ? speed : mode === "real_time" ? 1 : undefined, time_zone: tz }));

  return (
    <div className="stack">
      <Panel title={<>Game clock <EnvBadge env={env} /></>} sub="Holds expire, daily limits reset and daily jobs run on game time, not wall-clock time.">
        <ErrorNotice error={q.error} onRetry={q.reload} />
        {!c && q.loading && <Loading />}
        {c && (
          <div className="stack" style={{ gap: 6 }}>
            <div style={{ fontSize: "1.9rem", fontWeight: 700 }}>{formatGameTime(c.game_time, c.time_zone)}</div>
            <p className="muted">
              Game day {c.game_day} in {c.time_zone}.{" "}
              {c.mode === "real_time" ? "Running at real time." : c.mode === "scaled" ? `Running ${c.speed}× faster than real time.` : "Stopped: moves only when you advance it."}
            </p>
          </div>
        )}
      </Panel>
      <div className="grid-2">
        <Panel title="Change how time runs">
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (await save.run()) {
                invalidate(`dev:clock:${game.id}`);
                toast("Clock updated");
              }
            }}
          >
            <Segmented<Mode>
              label="Clock mode"
              value={mode}
              onChange={setMode}
              options={[{ value: "real_time", label: "Real time" }, { value: "scaled", label: "Faster" }, { value: "manual", label: "Manual" }]}
            />
            {mode === "scaled" && (
              <Field label="Speed" hint={`${speed} game seconds pass every real second. 1,440 makes a game day last one real minute.`}>
                <Input type="number" min={1} max={1000000} value={speed} onChange={(e) => setSpeed(Number(e.target.value))} />
              </Field>
            )}
            <Field label="Time zone" hint="IANA name, e.g. Africa/Lagos. Sets when a game day starts.">
              <Input value={tz} onChange={(e) => setTz(e.target.value)} />
            </Field>
            <ErrorNotice error={save.error} />
            <div><Button type="submit" variant="primary" busy={save.pending}>Save clock</Button></div>
          </form>
        </Panel>
        <AdvancePanel gameId={game.id} env={env} manual={c?.mode === "manual"} />
      </div>
    </div>
  );
}

export function AdvancePanel({ gameId, env, manual }: { gameId: string; env: Env; manual: boolean }) {
  const api = gameApi(useClient(), { gameId, env });
  const toast = useToast();
  const advance = useAction((body: { hours?: number; days?: number }) => api.advanceClock(body));
  const go = async (body: { hours?: number; days?: number }, label: string) => {
    const r = await advance.run(body);
    if (r) {
      invalidate(`dev:clock:${gameId}`);
      invalidate("sandbox:");
      toast(`Advanced ${label}${r.jobs.holds_expired ? `; ${r.jobs.holds_expired} hold${r.jobs.holds_expired === 1 ? "" : "s"} expired` : ""}`);
    }
  };
  return (
    <Panel title="Move time forward" sub="Only in manual mode. Due holds expire and daily jobs run straight away.">
      {!manual && <p className="muted small" style={{ marginBottom: 12 }}>Switch the clock to manual to use these.</p>}
      <div className="row">
        <Button disabled={!manual} busy={advance.pending} onClick={() => go({ hours: 1 }, "1 hour")}>Advance 1 hour</Button>
        <Button disabled={!manual} busy={advance.pending} onClick={() => go({ hours: 6 }, "6 hours")}>Advance 6 hours</Button>
        <Button disabled={!manual} busy={advance.pending} onClick={() => go({ days: 1 }, "1 day")}>Advance 1 day</Button>
      </div>
      <div style={{ marginTop: 12 }}><ErrorNotice error={advance.error} /></div>
    </Panel>
  );
}
